import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { Key, useEffect, useState } from "react";
import { toast } from "react-toastify";

import LoadingDialog from "../../components/LoadingDialog/LoadingDialog";
import { BooleanDictionary } from "../../GlobalTypes";
import { DeliveryAddressModel } from "../../models/DeliveryAddressModel";
import { DeviceCategoryModel } from "../../models/DeviceCategoryModel";
import { DeviceTypeModel } from "../../models/DeviceTypeModel";
import { PartnerModel } from "../../models/PartnerModel";
import { DeviceNeedAddRequestModel, DeviceNeedItemAddRequestModel } from "../../models/RequestModels";
import ResponseModel from "../../models/ResponseModel";
import DeviceCategoryService from "../../services/deviceCategories/DeviceCategoryService";
import DeviceRequestService from "../../services/deviceRequests/DeviceRequestService";
import DeviceTypeService from "../../services/deviceTypes/DeviceTypeService";
import PartnerService from "../../services/partners/PartnerService";
import { doneChecker, getLongAddress, hasApiError } from "../../utils/Utils";

import { style } from "./CreateDeviceRequest.style";

type DeviceLineItem = {
  categoryCode: string;
  typeCode: string;
  quantityInput: string;
  availableTypes: DeviceTypeModel[];
};

type CreateDeviceRequestProps = {
  setDialogOpen: (value: boolean) => void;
  getDeviceRequests?: () => void | Promise<void>;
};

type CreateDeviceRequestDone = BooleanDictionary & {
  partners: boolean;
  categories: boolean;
};

const CreateDeviceRequest = (props: CreateDeviceRequestProps): JSX.Element => {
  const MIN_QUANTITY: number = 1;
  const MAX_QUANTITY: number = 10;

  const [partners, setPartners] = useState<PartnerModel[]>([]);
  const [deliveryAddresses, setDeliveryAddresses] = useState<DeliveryAddressModel[]>([]);
  const [categories, setCategories] = useState<DeviceCategoryModel[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<PartnerModel | undefined>(undefined);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState<DeliveryAddressModel | undefined>(undefined);
  const [lineItems, setLineItems] = useState<DeviceLineItem[]>([
    { categoryCode: "-1", typeCode: "-1", quantityInput: MIN_QUANTITY.toString(), availableTypes: [] },
  ]);
  const [done, setDone] = useState<CreateDeviceRequestDone>({ partners: false, categories: false });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  //getting all the categories
  const getCategories = async (): Promise<void> => {
    const response: ResponseModel = await DeviceCategoryService.getDeviceCategories();

    if (hasApiError(response)) return;

    setCategories(response.data as DeviceCategoryModel[]);

    if (!done.categories)
      setDone((prev: CreateDeviceRequestDone): CreateDeviceRequestDone => ({ ...prev, categories: true }));
  };

  //getting all the partners
  const getPartners = async (): Promise<void> => {
    const response: ResponseModel = await PartnerService.getPartners({});

    if (hasApiError(response)) return;

    setPartners(response.data as PartnerModel[]);

    if (!done.partners)
      setDone((prev: CreateDeviceRequestDone): CreateDeviceRequestDone => ({ ...prev, partners: true }));
  };

  //on partner update, set up the new addresses
  const getDeliveryAddresses = (): void => {
    setDeliveryAddresses(
      partners.find((partner: PartnerModel): boolean => partner.partnerCode === selectedPartner?.partnerCode)
        ?.deliveryAddresses as DeliveryAddressModel[],
    );
  };

  //partner select handler
  const onPartnerSelect = (e: SelectChangeEvent): void => {
    setSelectedPartner(
      partners.find(
        (partner: PartnerModel): boolean => partner.partnerCode === parseInt(e.target.value),
      ) as PartnerModel,
    );
    setSelectedDeliveryAddress(undefined);
  };

  //address select handler
  const onDeliveryAddressSelect = (e: SelectChangeEvent): void => {
    setSelectedDeliveryAddress(
      deliveryAddresses.find(
        (address: DeliveryAddressModel): boolean => address.deliveryAddressCode === parseInt(e.target.value),
      ) as DeliveryAddressModel,
    );
  };

  //category select handler for a line item
  const onCategorySelect = async (e: SelectChangeEvent, index: number): Promise<void> => {
    const categoryCode: string = e.target.value;
    const newItems: DeviceLineItem[] = [...lineItems];
    newItems[index] = {
      ...newItems[index],
      categoryCode: categoryCode,
      typeCode: "-1",
      quantityInput: newItems[index].quantityInput,
      availableTypes: [],
    };
    setLineItems(newItems);

    if (categoryCode !== "-1") {
      const response: ResponseModel = await DeviceTypeService.getDeviceTypes({
        categoryId: parseInt(categoryCode),
      });

      if (hasApiError(response)) return;

      const types: DeviceTypeModel[] = response.data as DeviceTypeModel[];
      setLineItems((prev: DeviceLineItem[]): DeviceLineItem[] => {
        const updated: DeviceLineItem[] = [...prev];
        updated[index] = { ...updated[index], availableTypes: types };
        return updated;
      });
    }
  };

  //type select handler for a line item
  const onTypeSelect = (e: SelectChangeEvent, index: number): void => {
    const newItems: DeviceLineItem[] = [...lineItems];
    newItems[index] = { ...newItems[index], typeCode: e.target.value };
    setLineItems(newItems);
  };

  //quantity change handler for a line item
  const onQuantityChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number): void => {
    const newItems: DeviceLineItem[] = [...lineItems];
    newItems[index] = { ...newItems[index], quantityInput: e.target.value };
    setLineItems(newItems);
  };

  //quantity blur handler for a line item
  const onQuantityBlur = (index: number): void => {
    const value: number = parseInt(lineItems[index].quantityInput);
    const normalized: number = Number.isNaN(value)
      ? MIN_QUANTITY
      : Math.min(MAX_QUANTITY, Math.max(MIN_QUANTITY, value));
    const newItems: DeviceLineItem[] = [...lineItems];
    newItems[index] = { ...newItems[index], quantityInput: normalized.toString() };
    setLineItems(newItems);
  };

  //add new line item
  const addLineItem = (): void => {
    setLineItems([
      ...lineItems,
      { categoryCode: "-1", typeCode: "-1", quantityInput: MIN_QUANTITY.toString(), availableTypes: [] },
    ]);
  };

  //remove line item
  const removeLineItem = (index: number): void => {
    if (lineItems.length <= 1) {
      toast.error("Legalább egy sort meg kell adni");
      return;
    }
    const newItems: DeviceLineItem[] = lineItems.filter((_: DeviceLineItem, i: number): boolean => i !== index);
    setLineItems(newItems);
  };

  //event for registration button
  const onRegisterButton = async (): Promise<void> => {
    // guards
    if (selectedDeliveryAddress === undefined) {
      toast.error("Nem adtál meg szállítási címet");
      return;
    }

    // validate line items
    for (let i: number = 0; i < lineItems.length; i++) {
      if (lineItems[i].categoryCode === "-1") {
        toast.error(`A(z) ${i + 1}. sorban nem adtál meg eszköz típust`);
        return;
      }
      if (lineItems[i].typeCode === "-1") {
        toast.error(`A(z) ${i + 1}. sorban nem adtál meg termék típust`);
        return;
      }
      const quantity: number = parseInt(lineItems[i].quantityInput);
      if (Number.isNaN(quantity) || quantity < MIN_QUANTITY || quantity > MAX_QUANTITY) {
        toast.error(`A(z) ${i + 1}. sorban ${MIN_QUANTITY} és ${MAX_QUANTITY} közötti darabszámot kell megadni`);
        return;
      }
    }

    setIsSubmitting(true);

    const loadingToastId = toast.loading("Igény leadása folyamatban...");

    // build items array from all line items
    const items: DeviceNeedItemAddRequestModel[] = lineItems.map(
      (item: DeviceLineItem): DeviceNeedItemAddRequestModel => ({
        deviceTypeId: parseInt(item.typeCode),
        number: Math.min(MAX_QUANTITY, Math.max(MIN_QUANTITY, parseInt(item.quantityInput))),
      }),
    );

    const data: DeviceNeedAddRequestModel = {
      deliveryAddressCode: selectedDeliveryAddress.deliveryAddressCode,
      items,
    };

    const response: ResponseModel = await DeviceRequestService.addDeviceRequest(data);

    setIsSubmitting(false);

    if (hasApiError(response)) {
      toast.dismiss(loadingToastId);
      return;
    }

    toast.update(loadingToastId, {
      render: "Sikeres igény leadás",
      type: "success",
      isLoading: false,
      autoClose: 4000,
    });
    props.setDialogOpen(false);
    if (props.getDeviceRequests) void props.getDeviceRequests();
  };

  //get data on load
  useEffect((): void => {
    void getCategories();
    void getPartners();
  }, []);

  //reload the addresses when the partner changes
  useEffect((): void => {
    if (selectedPartner !== undefined) {
      getDeliveryAddresses();
    } else {
      setSelectedDeliveryAddress(undefined);
      setDeliveryAddresses([]);
    }
  }, [selectedPartner]);

  // blocking with a dialog while getting all the data
  if (!doneChecker(done)) {
    return <LoadingDialog />;
  }

  return (
    <Paper style={style.paper}>
      <Typography variant="h3" align="center" marginBottom={8}>
        Új eszköz igénylés
      </Typography>
      <Grid container spacing={4}>
        {/* Partner select */}
        <Grid item sm={6} md={6} xs={12}>
          <Typography style={style.label}>Ügyfél</Typography>
          <Select fullWidth value={selectedPartner?.partnerCode.toString() ?? "-1"} onChange={onPartnerSelect}>
            <MenuItem value={"-1"} key={-1}>
              Nincs kiválasztva
            </MenuItem>
            {partners.map((partner: PartnerModel, i: Key): JSX.Element => {
              return (
                <MenuItem value={partner.partnerCode} key={i}>
                  {partner.name}
                </MenuItem>
              );
            })}
          </Select>
        </Grid>

        {/* Delivery Address select */}
        <Grid item sm={6} md={6} xs={12}>
          <Typography style={style.label}>Telephely</Typography>
          <FormControl style={style.formControl}>
            <Select
              fullWidth
              defaultValue={"-1"}
              onChange={onDeliveryAddressSelect}
              value={selectedDeliveryAddress?.deliveryAddressCode.toString() ?? "-1"}
            >
              <MenuItem value={"-1"} key={-1}>
                Nincs kiválasztva
              </MenuItem>
              {deliveryAddresses.map((address: DeliveryAddressModel, i: Key): JSX.Element => {
                return (
                  <MenuItem value={address.deliveryAddressCode} key={i}>
                    {getLongAddress(address, true)}
                  </MenuItem>
                );
              })}
            </Select>
            {!selectedPartner && <FormHelperText>Először válassz ügyfelet</FormHelperText>}
          </FormControl>
        </Grid>
      </Grid>

      {/* Line items section */}
      <Typography variant="h5" marginTop={4} marginBottom={2}>
        Igényelt eszközök
      </Typography>
      {lineItems.map(
        (item: DeviceLineItem, index: number): JSX.Element => (
          <Grid container spacing={2} key={index} alignItems="center" marginBottom={2}>
            {/* Category select */}
            <Grid item sm={5} md={5} xs={12}>
              <Typography style={style.label}>Eszközök kategóriája</Typography>
              <Select
                fullWidth
                value={item.categoryCode}
                onChange={(e: SelectChangeEvent): void => {
                  void onCategorySelect(e, index);
                }}
              >
                <MenuItem value={"-1"} key={-1}>
                  Nincs kiválasztva
                </MenuItem>
                {categories.map((category: DeviceCategoryModel, i: Key): JSX.Element => {
                  return (
                    <MenuItem value={category.id} key={i}>
                      {category.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </Grid>

            {/* Type select (filtered by category) */}
            <Grid item sm={4} md={4} xs={12}>
              <Typography style={style.label}>Eszköz típus</Typography>
              <Select fullWidth value={item.typeCode} onChange={(e: SelectChangeEvent): void => onTypeSelect(e, index)}>
                <MenuItem value={"-1"} key={-1}>
                  Nincs kiválasztva
                </MenuItem>
                {item.availableTypes.map((type: DeviceTypeModel, i: Key): JSX.Element => {
                  return (
                    <MenuItem value={type.id} key={i}>
                      {type.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </Grid>

            {/* Quantity input */}
            <Grid item sm={2} md={2} xs={6}>
              <Typography style={style.label}>Darabszám</Typography>
              <TextField
                fullWidth
                type="number"
                value={item.quantityInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void =>
                  onQuantityChange(e, index)
                }
                onBlur={(): void => onQuantityBlur(index)}
                inputProps={{ min: MIN_QUANTITY, max: MAX_QUANTITY, step: 1 }}
              />
            </Grid>

            {/* Delete line */}
            <Grid item sm={1} md={1} xs={6}>
              <Typography style={style.label}>Törlés</Typography>
              <IconButton size="large" color="error" onClick={(): void => removeLineItem(index)}>
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ),
      )}

      <Button variant="outlined" onClick={addLineItem} style={{ marginTop: 8 }}>
        Hozzáadás
      </Button>

      {/* Buttons */}
      <Box style={style.buttonBox}>
        <Button variant="contained" color="error" onClick={(): void => props.setDialogOpen(false)}>
          Vissza
        </Button>
        <Button
          variant="contained"
          onClick={(): void => {
            void onRegisterButton();
          }}
          style={style.button}
          disabled={isSubmitting}
        >
          Mentés
        </Button>
      </Box>
    </Paper>
  );
};

export default CreateDeviceRequest;
